import React, { useEffect, useRef, useState } from 'react';

const FOCUSABLE_ELEMENT_SELECTOR = 'button, a[href], tabindex:not([tabindex="-1"])';

enum KeyboardKey {
  ENTER = 'Enter',
  ESC = 'Escape',
  ARROW_DOWN = 'ArrowDown',
  ARROW_UP = 'ArrowUp',
  ARROW_LEFT = 'ArrowLeft',
  ARROW_RIGHT = 'ArrowRight',
  TAB = 'Tab',
}

enum Section {
  SUBSECTION = 'subsection',
  FIRST_SUBSECTION = 'subsection-0',
}

const isSubsection = (id: string): boolean => id.includes(Section.SUBSECTION);
const isFirstSubsection = (id: string): boolean => id.includes(Section.FIRST_SUBSECTION);

/**
 *
 * @param navItems the navigation items to go through
 * @param index the index of the item to focus
 * @param callback optional callback to execute after finding the item
 * @description Checks if nav item is a button or anchor. If not, we assume it's wrapped and look for one in the element.
 */

const handleNextMenuLevel = (
  parentMenu: HTMLElement,
  focusableElement: HTMLElement,
  callback?: (submenuId: string) => void
): void => {
  setTimeout(() => {
    const submenuId = focusableElement.getAttribute('aria-controls');
    if (!parentMenu || !submenuId) {
      return;
    }

    const closestNextSubmenuId = submenuId.includes(' ')
      ? submenuId.split(' ')[0]
      : submenuId;
    const submenu = parentMenu.querySelector(`#${closestNextSubmenuId}`) as HTMLElement;
    if (!submenu) {
      return;
    }

    const firstFocusableElement = submenu.querySelector(
      FOCUSABLE_ELEMENT_SELECTOR
    ) as HTMLElement;
    if (firstFocusableElement) {
      firstFocusableElement.focus();
      callback?.(closestNextSubmenuId);
    }
  }, 150);
};

const useNavKeyboardControl = <T extends HTMLElement>(): React.RefObject<T> => {
  const navListRef = useRef<T | null>(null);
  const focusIndex = useRef<number | null>(0);
  /* 
      Momentarily we have 3 levels for the megamenu:
        - 0 - The main header navigation items
        - 1 - The items inside the opened megamenu
        - 2 - The subsections of the opened megamenu sections
   */
  const [megamenuLevel, setMegamenuLevel] = useState<number>(0);
  const [submenuId, setSubmenuId] = useState<string | undefined>(undefined);
  const [sectionIndex, setSectionIndex] = useState<number>(0);

  const closeMegamenu = (resetIndex = true): void => {
    if (!navListRef.current) {
      return;
    }
    const expandedMegamenuButton = navListRef.current.querySelector(
      'button[aria-expanded="true"]'
    ) as HTMLElement;
    if (!expandedMegamenuButton) {
      return;
    }
    expandedMegamenuButton.click();
    setMegamenuLevel(0);
    setSubmenuId(undefined);
    setSectionIndex(0);
    if (resetIndex) {
      focusIndex.current = 0;
      return;
    }
    const mainNavItems = Array.from(
      navListRef.current.querySelectorAll('button[aria-controls]')
    ).map((navItem) => navItem.getAttribute('aria-controls'));
    const index = mainNavItems.indexOf(submenuId ?? '');
    focusIndex.current = index === -1 ? 0 : index;
  };

  const getSelectedSubmenu = (
    level: number,
    ariaControl?: string,
    nextSectionIndex?: number
  ): HTMLElement | null => {
    const navList = navListRef.current;
    if (!navList) {
      console.error('Megamenu header not found.');
      return null;
    }

    // No submenu is supposed to be open, so we return the header nav list
    if (level === 0 || !ariaControl) {
      return navList;
    }

    const id =
      isSubsection(ariaControl) && nextSectionIndex !== undefined
        ? ariaControl.replace(`${sectionIndex}`, `${nextSectionIndex}`)
        : ariaControl;

    const submenu = navList.querySelector(`#${id}`) as HTMLElement;
    if (!submenu) {
      console.error(`The ${id} submenu was supposed to be opened, but was not found.`);
      return null;
    }

    const list = submenu.querySelectorAll('ul');

    if (!list || list.length === 0) {
      console.error(`The submenu ${id} does not contain any list.`);
      return null;
    }

    return list[isSubsection(id) ? 0 : nextSectionIndex ?? sectionIndex] as HTMLElement;
  };

  const findNavItem = (
    navItems: Element[],
    callback?: (element: HTMLElement) => void
  ): HTMLElement | undefined => {
    if (focusIndex.current === null) {
      return;
    }

    const element = navItems[focusIndex.current].firstElementChild as HTMLElement;
    if (!element) {
      console.error(
        'No focusable element found in the nav item. Please make sure the element is or has a button or anchor.'
      );
      return;
    }

    if (
      (element.tagName === 'A' && element?.getAttribute('href')) ||
      element.tagName === 'BUTTON'
    ) {
      callback?.(element);
      return element;
    }

    const interactableElement = element.querySelector(
      FOCUSABLE_ELEMENT_SELECTOR
    ) as HTMLElement;
    if (interactableElement) {
      callback?.(interactableElement);
      return interactableElement;
    }

    console.error(
      'No focusable element found in the nav item. Please make sure the element is or has a button or anchor.'
    );
  };

  const handleSubsectionExit = (): void => {
    const megamenuId = navListRef.current
      ?.querySelector('button[aria-expanded="true"]')
      ?.getAttribute('aria-controls');
    if (!megamenuId) {
      return;
    }
    const megamenu = navListRef.current?.querySelector(`#${megamenuId}`) as HTMLElement;
    if (megamenu) {
      const firstFocusableElement = megamenu.querySelector(
        FOCUSABLE_ELEMENT_SELECTOR
      ) as HTMLElement;
      firstFocusableElement?.focus();
      focusIndex.current = 0;
      setMegamenuLevel((prev) => (prev -= 1));
      setSubmenuId(megamenuId);
      setSectionIndex(0);
    }
  };

  const focusNextMenu = (): void => {
    if (!submenuId || megamenuLevel === 0) {
      return;
    }
    const nextList = getSelectedSubmenu(megamenuLevel, submenuId, sectionIndex + 1);
    if (nextList) {
      setSectionIndex((prev) => prev + 1);
      if (isSubsection(submenuId)) {
        setSubmenuId((prev) => prev?.replace(`${sectionIndex}`, `${sectionIndex + 1}`));
      }
      (nextList.querySelector(FOCUSABLE_ELEMENT_SELECTOR) as HTMLElement)?.focus();
      focusIndex.current = 0;
      return;
    }
    if (megamenuLevel === 1) {
      closeMegamenu(false);
      return;
    }
    const firstList = getSelectedSubmenu(megamenuLevel, submenuId, 0);
    if (!firstList) {
      return;
    }
    setSectionIndex(0);
    if (isSubsection(submenuId)) {
      setSubmenuId((prev) => prev?.replace(`${sectionIndex}`, `0`));
    }
    (firstList.querySelector(FOCUSABLE_ELEMENT_SELECTOR) as HTMLElement)?.focus();
    focusIndex.current = 0;
    return;
  };

  // Focuses the next item in the section list.
  // If we're at the end of the list, we're looking for the next section list
  // and focusing on the first element in that list.
  const findNextItem = (
    currentMenu: HTMLElement,
    callback?: (element: HTMLElement) => void
  ): void => {
    const children = Array.from(currentMenu.children);
    if (children.length === 0) {
      focusIndex.current = null;
      return;
    }
    if (focusIndex.current === children.length - 1 && submenuId && megamenuLevel > 0) {
      focusNextMenu();
      return;
    }
    const newIndex =
      focusIndex.current === null
        ? 0
        : focusIndex.current === children.length - 1
        ? 0
        : focusIndex.current + 1;
    focusIndex.current = newIndex;
    findNavItem(children, (element) => callback?.(element));
  };

  const focusNextItem = (currentMenu: HTMLElement): void => {
    findNextItem(currentMenu, (element) => element.focus());
  };

  const focusPreviousMenu = (focusLastItem?: boolean): void => {
    if (!submenuId || megamenuLevel === 0) {
      return;
    }
    if (megamenuLevel === 1 && sectionIndex === 0) {
      closeMegamenu(false);
      return;
    }
    const nextList = getSelectedSubmenu(megamenuLevel, submenuId, sectionIndex - 1);
    if (nextList) {
      setSectionIndex((prev) => prev - 1);
      if (isSubsection(submenuId)) {
        setSubmenuId((prev) => prev?.replace(`${sectionIndex}`, `${sectionIndex - 1}`));
      }

      if (!focusLastItem) {
        (nextList.querySelector(FOCUSABLE_ELEMENT_SELECTOR) as HTMLElement)?.focus();
        focusIndex.current = 0;
        return;
      }

      const anchorElements = Array.from(
        nextList.querySelectorAll(FOCUSABLE_ELEMENT_SELECTOR)
      ) as HTMLElement[];
      anchorElements[anchorElements.length - 1]?.focus();
      focusIndex.current = anchorElements.length - 1;
      return;
    }
  };

  // Focuses the previous item in the section list.
  // If we're at the beginning of the list, we're looking for the previous section list
  // and focusing on the last element in that list.
  const findPreviousItem = (
    currentMenu: HTMLElement,
    callback?: (element: HTMLElement) => void
  ): void => {
    const children = Array.from(currentMenu.children);
    if (children.length === 0) {
      focusIndex.current = null;
      return;
    }
    if (focusIndex.current === 0 && submenuId && megamenuLevel > 0) {
      if (isFirstSubsection(submenuId) && megamenuLevel > 1) {
        handleSubsectionExit();
        return;
      }
      focusPreviousMenu(true);
      return;
    }
    const newIndex =
      focusIndex.current === null
        ? 0
        : focusIndex.current === 0
        ? children.length - 1
        : focusIndex.current - 1;
    focusIndex.current = newIndex;
    findNavItem(children, (element) => callback?.(element));
  };

  const focusPreviousItem = (currentMenu: HTMLElement): void => {
    findPreviousItem(currentMenu, (element) => element.focus());
  };

  // Keyboard controls:
  //   - Enter, Arrow Down - Open next level (arrow down only works for the header nav items)
  //   - Esc - Close the megamenu no matter the level
  //   - Arrow Up - Close the megamenu if level 1 section is opened
  //   - Arrow Right - Select next
  //   - Arrow Left - Select previous

  // When dropdown selected, the controls should be limited to these options
  const handleKeyboardControls = (
    e: KeyboardEvent,
    currentMenu: HTMLElement,
    parentMenu: HTMLElement
  ): void => {
    const children = Array.from(currentMenu.children);
    if (children.length === 0) return;

    const {
      ARROW_UP,
      ARROW_DOWN,
      ARROW_LEFT,
      ARROW_RIGHT,
      ENTER,
      ESC,
      TAB,
    } = KeyboardKey;
    if (e.key !== ENTER && e.key !== TAB) {
      e.preventDefault();
    }

    switch (e.key) {
      case ARROW_UP:
        if (megamenuLevel === 0) {
          const parentButton = navListRef.current?.querySelector(
            'button[aria-expanded="true"]'
          ) as HTMLElement;
          if (!parentButton) {
            break;
          }
          parentButton.click();
          break;
        }
        if (megamenuLevel === 1) {
          if (focusIndex.current === 0 && sectionIndex === 0) {
            closeMegamenu(false);
          } else {
            focusPreviousItem(currentMenu);
          }
        }
        break;
      case ARROW_LEFT:
        if (megamenuLevel === 0) {
          focusPreviousItem(parentMenu);
          break;
        }
        focusPreviousMenu();
        break;
      case ARROW_DOWN:
      case ENTER:
        if (e.key === ARROW_DOWN && megamenuLevel > 0) {
          focusNextItem(currentMenu);
          break;
        }
        findNavItem(children, (element) => {
          if (e.key === ARROW_DOWN && element.getAttribute('aria-expanded') === 'false') {
            element.click();
          }
          // Buttons/Anchors that control the menu have aria-controls, so we're advancing to the next level
          // if the element has aria-controls
          if (element.getAttribute('aria-controls')) {
            handleNextMenuLevel(parentMenu, element, (selectedSubmenu) => {
              setMegamenuLevel((prev) => (prev += 1));
              setSubmenuId(selectedSubmenu);
              focusIndex.current = 0;
            });
          }
        });
        break;
      case ARROW_RIGHT:
        if (megamenuLevel === 0) {
          focusNextItem(parentMenu);
          break;
        }
        focusNextMenu();
        break;
      case ESC:
        closeMegamenu();
        break;
    }
  };

  useEffect(() => {
    if (!navListRef.current) {
      return;
    }

    const onKeyDown = (e: KeyboardEvent): void => {
      const navList = navListRef.current;
      if (!navList) return;

      const currentSection = getSelectedSubmenu(megamenuLevel, submenuId);
      if (!currentSection) return;
      if (currentSection.contains(e.target as Node)) {
        e.stopPropagation();
        handleKeyboardControls(e, currentSection, navList);
      }
    };

    const navList = navListRef.current;
    if (!navList) {
      return;
    }

    navList.addEventListener('keydown', onKeyDown);

    return () => navList.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navListRef, megamenuLevel, submenuId, sectionIndex]);

  return navListRef;
};

export default useNavKeyboardControl;
